/**
 * auth-prompt-modal.js
 * Shared "please sign in to continue" prompt — a self-injecting singleton
 * modal (icon, title, message, Sign In / Create Account buttons, optional
 * footer hint), replacing the ~4 near-identical per-page implementations
 * (reviews, reviews/service, jobs/recruiting, events) that each hand-rolled
 * this same shell with a different CSS-class prefix.
 *
 * Not the same component as InteractionModals.showGuestLogin() — that one
 * is purpose-built for the leadership-page "contact a professional" flow
 * (its message template is hardcoded around a professionalName) and has
 * its own working caller; this is the generic version for any "you must
 * be signed in" moment.
 *
 * window.AuthPromptModal.show({
 *   icon,                 // optional emoji/text icon
 *   title,                // required
 *   message,              // required, HTML-safe string
 *   signInHref,           // required
 *   signInText,           // optional, default "Sign In"
 *   signInTarget,         // optional, e.g. "_blank"
 *   createAccountHref,    // optional — omit to hide the secondary button
 *   createAccountText,    // optional, default "Create Account"
 *   createAccountTarget,  // optional, e.g. "_blank"
 *   footerHint,           // optional extra line below the buttons
 * })
 * window.AuthPromptModal.close()
 */
(function () {
  "use strict";

  function injectStyles() {
    if (document.getElementById("apm-css")) return;
    const s = document.createElement("style");
    s.id = "apm-css";
    s.textContent = `
      .apm-modal {
        display: none; position: fixed; inset: 0; z-index: 10000;
        align-items: center; justify-content: center; padding: 1.5rem;
      }
      .apm-modal.active { display: flex; }
      .apm-backdrop {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.75); backdrop-filter: blur(8px);
      }
      .apm-container {
        position: relative; z-index: 1;
        background: var(--color-bg-card, #111827);
        border: 1px solid var(--border-color, rgba(255,255,255,0.1));
        border-radius: 20px; max-width: 440px; width: 100%;
        padding: 2.5rem 2rem; text-align: center;
      }
      .apm-close {
        position: absolute; top: 1rem; right: 1rem;
        width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;
        background: transparent; border: none; color: var(--color-text-secondary, rgba(255,255,255,0.5));
        font-size: 1.5rem; cursor: pointer; border-radius: 50%; transition: background 0.2s;
      }
      .apm-close:hover { background: rgba(255,255,255,0.08); }
      .apm-icon {
        width: 64px; height: 64px; margin: 0 auto 1.25rem;
        display: flex; align-items: center; justify-content: center;
        background: var(--accent-glow, rgba(16,185,129,0.12));
        border: 1px solid var(--accent-color, rgba(16,185,129,0.3));
        border-radius: 50%; font-size: 1.75rem;
      }
      .apm-icon:empty { display: none; }
      .apm-title { font-size: 1.3rem; font-weight: 700; color: var(--color-text-primary, #fff); margin: 0 0 0.75rem; }
      .apm-message { color: var(--color-text-secondary, rgba(255,255,255,0.65)); font-size: 0.9375rem; line-height: 1.6; margin: 0 0 1.75rem; }
      .apm-actions { display: flex; flex-direction: column; gap: 0.75rem; }
      .apm-btn {
        display: block; width: 100%; padding: 0.75rem 1rem; box-sizing: border-box;
        border-radius: 8px; font-weight: 600; font-size: 0.95rem; text-decoration: none;
        transition: opacity 0.2s;
      }
      .apm-btn:hover { opacity: 0.88; }
      .apm-btn-primary { background: var(--accent-color, #10b981); color: #0a0a0a; }
      .apm-btn-secondary { background: transparent; color: var(--color-text-primary, #fff); border: 1px solid var(--border-color, rgba(255,255,255,0.2)); }
      .apm-hint { font-size: 0.8rem; color: var(--color-text-muted, rgba(255,255,255,0.4)); margin: 1.25rem 0 0; }
      .apm-hint:empty { display: none; }
    `;
    document.head.appendChild(s);
  }

  let el = null;

  function close() {
    if (el) {
      el.classList.remove("active");
      document.body.style.overflow = "";
    }
  }

  function show(opts) {
    const {
      icon = "",
      title = "",
      message = "",
      signInHref = "",
      signInText = "Sign In",
      signInTarget = "_self",
      createAccountHref = null,
      createAccountText = "Create Account",
      createAccountTarget = "_self",
      footerHint = "",
    } = opts || {};

    injectStyles();

    if (!el) {
      el = document.createElement("div");
      el.className = "apm-modal";
      el.innerHTML = `
        <div class="apm-backdrop"></div>
        <div class="apm-container" role="dialog" aria-modal="true">
          <button class="apm-close" aria-label="Close">&times;</button>
          <div class="apm-icon" aria-hidden="true"></div>
          <h3 class="apm-title"></h3>
          <p class="apm-message"></p>
          <div class="apm-actions">
            <a class="apm-btn apm-btn-primary"></a>
            <a class="apm-btn apm-btn-secondary" style="display:none"></a>
          </div>
          <p class="apm-hint"></p>
        </div>
      `;
      document.body.appendChild(el);
      el.querySelector(".apm-backdrop").addEventListener("click", close);
      el.querySelector(".apm-close").addEventListener("click", close);
      document.addEventListener("keydown", function onKey(e) {
        if (e.key === "Escape" && el?.classList.contains("active")) close();
      });
    }

    el.querySelector(".apm-icon").textContent = icon;
    el.querySelector(".apm-title").textContent = title;
    el.querySelector(".apm-message").innerHTML = message;

    const primaryBtn = el.querySelector(".apm-btn-primary");
    primaryBtn.href = signInHref;
    primaryBtn.target = signInTarget;
    if (signInTarget === "_blank") primaryBtn.rel = "noopener noreferrer";
    primaryBtn.textContent = signInText;

    const secondaryBtn = el.querySelector(".apm-btn-secondary");
    if (createAccountHref) {
      secondaryBtn.href = createAccountHref;
      secondaryBtn.target = createAccountTarget;
      if (createAccountTarget === "_blank") {
        secondaryBtn.rel = "noopener noreferrer";
      }
      secondaryBtn.textContent = createAccountText;
      secondaryBtn.style.display = "";
    } else {
      secondaryBtn.style.display = "none";
    }

    el.querySelector(".apm-hint").textContent = footerHint;

    el.classList.add("active");
    document.body.style.overflow = "hidden";
  }

  window.AuthPromptModal = { show, close };
})();
